import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Film,
  Fullscreen,
  MonitorCheck,
  PanelRight,
  Play,
  StickyNote
} from 'lucide-react'
import type { CSSProperties } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Player, type PlayerRef } from '@remotion/player'
import { AuthControls } from './AuthControls'
import { DeckTimeline } from './DeckTimeline'
import { StudioBookend, StudioDeckTimeline } from './StudioDeckTimeline'
import {
  getStudioTimelineDuration,
  studioIntroFrames,
  studioOutroFrames
} from './studio-timeline'
import type { AuthState } from './auth'
import { isManualAmbient, resolveTheme, setAmbientFrame, useAmbientClockDriver } from './themes'
import type { DeckBundle } from './types'
import { DeckOutro } from './DeckOutro'

const fps = 30
const settledFrameOffset = 112

/**
 * Frame-exact control for scripts/capture-deck-for-video.mjs. Only installed
 * when the page is opened with `?ambient=manual`, which also stops the ambient
 * clock so the script owns both clocks.
 */
type DeckCaptureHandle = {
  theme: string
  slideStarts: number[]
  settledFrames: number[]
  /** Moves both players to a deck frame (not counting the studio intro). */
  seek: (frame: number) => void
  setAmbientFrame: (frame: number) => void
}

declare global {
  interface Window {
    __deckCapture?: DeckCaptureHandle
  }
}

type ViewerMode = 'audience' | 'studio'
type StudioCue = 'hook' | 'signoff' | null

export function DeckViewer({
  deck,
  initialMode,
  auth,
  onBack,
  onOpenStudio,
  onAuthChange
}: {
  deck: DeckBundle
  initialMode: ViewerMode
  auth: AuthState
  onBack: () => void
  onOpenStudio: () => void
  onAuthChange: (auth: AuthState) => void
}) {
  const [slideIndex, setSlideIndex] = useState(() => getInitialSlideIndex(deck))
  // The slide the presenter is now on. `slideIndex` only commits once the
  // entrance animation has finished playing, which is several seconds on a long
  // slide — too late for the notes panel, where the speaker reads the demo cue
  // for the slide already on screen. This one moves as soon as navigation starts.
  const [notesIndex, setNotesIndex] = useState(() => getInitialSlideIndex(deck))
  const [mode, setMode] = useState<ViewerMode>(initialMode)
  const [studioCue, setStudioCue] = useState<StudioCue>(
    initialMode === 'studio' ? 'hook' : null
  )
  const [isTimelinePlaying, setIsTimelinePlaying] = useState(false)
  // Deck canvas. Most decks use the 1280x1080 studio canvas; a deck can opt into
  // a full 16:9 surface with `canvas: wide` in deck.yaml.
  const canvas = deck.meta.canvas === 'wide' ? { width: 1920, height: 1080 } : { width: 1280, height: 1080 }

  const isStudio = mode === 'studio'
  const isStudioRoute = initialMode === 'studio'
  // Tracks the slide we are heading to, updated synchronously on every
  // navigation so a key/click mid-animation advances from the in-flight target
  // instead of the last-committed slide.
  const targetIndexRef = useRef(slideIndex)
  const playerRef = useRef<PlayerRef>(null)
  const recordingPlayerRef = useRef<PlayerRef>(null)
  const recordingSurfaceRef = useRef<HTMLDivElement>(null)
  const slideFrameRef = useRef<HTMLDivElement>(null)
  const smoothSeekRef = useRef<number | null>(null)
  // Cross-window navigation. The slide is projected fullscreen, so the notes
  // (demo cues) have to live in a second window that follows along.
  const navChannelRef = useRef<BroadcastChannel | null>(null)
  const applyingRemoteNavRef = useRef(false)
  const studioPlaybackResetRef = useRef(false)
  const slideStarts = useMemo(() => getSlideStarts(deck), [deck])
  const totalFrames = useMemo(
    () =>
      deck.meta.slides.reduce(
        (sum, slide) => sum + (slide.durationInFrames ?? 120),
        0
      ),
    [deck]
  )
  const studioTotalFrames = useMemo(() => getStudioTimelineDuration(deck), [deck])
  const theme = useMemo(() => resolveTheme(deck.meta), [deck.meta])
  useAmbientClockDriver(theme.ambient, fps)

  const getSlideSettledFrame = useCallback(
    (index: number) => {
      const duration = deck.meta.slides[index]?.durationInFrames ?? 120
      return (slideStarts[index] ?? 0) + Math.min(settledFrameOffset, Math.max(0, duration - 1))
    },
    [deck.meta.slides, slideStarts]
  )

  const setPlayersFrame = useCallback((frame: number) => {
    playerRef.current?.seekTo(frame + (isStudio ? studioIntroFrames : 0))
    recordingPlayerRef.current?.seekTo(frame + studioIntroFrames)
  }, [isStudio])

  const pausePlayers = useCallback(() => {
    playerRef.current?.pause()
    recordingPlayerRef.current?.pause()
  }, [])

  const animatePlayersToFrame = useCallback(
    (fromFrame: number, toFrame: number, onComplete: () => void) => {
      pausePlayers()
      if (smoothSeekRef.current !== null) {
        window.cancelAnimationFrame(smoothSeekRef.current)
      }

      setPlayersFrame(fromFrame)
      // Advance at the deck's native frame rate so arrow-key navigation plays
      // the entrance at the same speed as the "再生" (play) button — not a fast
      // scrub. Linear progress keeps it 1:1 with real playback.
      const durationMs = Math.max(1, (Math.abs(toFrame - fromFrame) / fps) * 1000)
      const startedAt = performance.now()

      const step = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / durationMs)
        setPlayersFrame(Math.round(fromFrame + (toFrame - fromFrame) * progress))

        if (progress < 1) {
          smoothSeekRef.current = window.requestAnimationFrame(step)
          return
        }

        smoothSeekRef.current = null
        setPlayersFrame(toFrame)
        onComplete()
      }

      smoothSeekRef.current = window.requestAnimationFrame(step)
    },
    [pausePlayers, setPlayersFrame]
  )

  useEffect(() => {
    if (!isManualAmbient()) {
      return undefined
    }

    window.__deckCapture = {
      theme: theme.id,
      slideStarts,
      settledFrames: deck.meta.slides.map((_, index) => getSlideSettledFrame(index)),
      seek: (frame) => {
        if (smoothSeekRef.current !== null) {
          window.cancelAnimationFrame(smoothSeekRef.current)
          smoothSeekRef.current = null
        }
        pausePlayers()
        setPlayersFrame(frame)
      },
      setAmbientFrame: (frame) => setAmbientFrame(Math.max(0, Math.round(frame)))
    }

    return () => {
      delete window.__deckCapture
    }
  }, [deck.meta.slides, getSlideSettledFrame, pausePlayers, setPlayersFrame, slideStarts, theme.id])

  const goToSlide = useCallback(
    (index: number) => {
      setIsTimelinePlaying(false)
      const nextIndex = Math.max(0, Math.min(deck.meta.slides.length - 1, index))
      const returningFromCue = studioCue !== null
      setStudioCue(null)

      // Compare against the in-flight target (not the committed slide) so
      // navigation works even while an entrance animation is still playing.
      if (nextIndex === targetIndexRef.current && !returningFromCue) {
        return
      }
      targetIndexRef.current = nextIndex
      setNotesIndex(nextIndex)
      if (!applyingRemoteNavRef.current) {
        navChannelRef.current?.postMessage({ type: 'goto', index: nextIndex })
      }

      // Always start from the destination slide's first frame and play its
      // entrance through to the settled frame, mirroring the play button.
      const startFrame = slideStarts[nextIndex] ?? getSlideSettledFrame(nextIndex)
      const targetFrame = getSlideSettledFrame(nextIndex)

      animatePlayersToFrame(startFrame, targetFrame, () => {
        setSlideIndex(nextIndex)
        window.history.replaceState(null, '', `#${nextIndex + 1}`)
      })
    },
    [
      animatePlayersToFrame,
      deck.meta.slides.length,
      getSlideSettledFrame,
      slideStarts,
      studioCue
    ]
  )

  // Keep the projected window and a presenter-notes window on the same slide.
  // At an event the slide runs fullscreen, so the demo cues in the notes have to
  // be read from a second window (other screen) that follows the projection.
  const goToSlideRef = useRef(goToSlide)
  const studioCueRef = useRef(studioCue)

  useEffect(() => {
    goToSlideRef.current = goToSlide
    studioCueRef.current = studioCue
  }, [goToSlide, studioCue])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return
    }

    const channel = new BroadcastChannel(`deck-nav:${deck.meta.slug}`)
    navChannelRef.current = channel
    channel.onmessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; index?: number } | null
      if (!data || data.type !== 'goto' || typeof data.index !== 'number') {
        return
      }
      // A window sitting on a studio bookend still has to move, even when the
      // incoming index matches the slide it last committed.
      if (data.index === targetIndexRef.current && studioCueRef.current === null) {
        return
      }

      applyingRemoteNavRef.current = true
      try {
        goToSlideRef.current(data.index)
      } finally {
        applyingRemoteNavRef.current = false
      }
    }

    return () => {
      channel.close()
      navChannelRef.current = null
    }
  }, [deck.meta.slug])

  // Opens the same deck in studio layout, where the notes panel lives. Reused
  // by name so a second click focuses the window instead of stacking copies.
  const openPresenterNotes = useCallback(() => {
    window.open(
      `/decks/${deck.meta.slug}/studio`,
      `deck-notes-${deck.meta.slug}`,
      'width=980,height=900'
    )
  }, [deck.meta.slug])

  const showStudioHook = useCallback(() => {
    setIsTimelinePlaying(false)
    pausePlayers()
    setStudioCue('hook')
    playerRef.current?.seekTo(0)
    recordingPlayerRef.current?.seekTo(0)
  }, [pausePlayers])

  const showStudioSignoff = useCallback(() => {
    setIsTimelinePlaying(false)
    pausePlayers()
    const signoffFrame = studioIntroFrames + totalFrames + Math.min(30, studioOutroFrames - 1)
    setStudioCue('signoff')
    playerRef.current?.seekTo(signoffFrame)
    recordingPlayerRef.current?.seekTo(signoffFrame)
  }, [pausePlayers, totalFrames])

  const goRelative = useCallback(
    (delta: number) => {
      if (isStudioRoute && auth.canRecord && isStudio) {
        if (studioCue === 'hook') {
          if (delta > 0) {
            goToSlide(0)
          }
          return
        }

        if (studioCue === 'signoff') {
          if (delta < 0) {
            goToSlide(deck.meta.slides.length - 1)
          }
          return
        }

        const nextIndex = targetIndexRef.current + delta
        if (nextIndex < 0) {
          showStudioHook()
          return
        }
        if (nextIndex >= deck.meta.slides.length) {
          showStudioSignoff()
          return
        }
      }

      goToSlide(targetIndexRef.current + delta)
    },
    [
      auth.canRecord,
      deck.meta.slides.length,
      goToSlide,
      isStudio,
      isStudioRoute,
      showStudioHook,
      showStudioSignoff,
      studioCue
    ]
  )

  useEffect(() => {
    if (studioCue !== null) {
      return
    }

    if (studioPlaybackResetRef.current) {
      studioPlaybackResetRef.current = false
      return
    }

    const settledFrame = getSlideSettledFrame(slideIndex)
    setPlayersFrame(settledFrame)
  }, [getSlideSettledFrame, setPlayersFrame, slideIndex, studioCue])

  useEffect(() => {
    return () => {
      if (smoothSeekRef.current !== null) {
        window.cancelAnimationFrame(smoothSeekRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // While the click-to-edit panel (or any other field) has focus, the arrow
      // keys belong to the caret, and space is a space. Sending the deck to
      // another slide there would also strand the panel on copy that is no
      // longer on screen.
      if (isEditableTarget(event.target)) {
        return
      }

      if (event.key === 'ArrowRight' || event.key === 'PageDown' || event.key === ' ') {
        event.preventDefault()
        goRelative(1)
      }

      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault()
        goRelative(-1)
      }

      if (event.key === 'Home') {
        event.preventDefault()
        if (isStudioRoute && auth.canRecord && isStudio) {
          showStudioHook()
        } else {
          goToSlide(0)
        }
      }

      if (event.key === 'End') {
        event.preventDefault()
        if (isStudioRoute && auth.canRecord && isStudio) {
          showStudioSignoff()
        } else {
          goToSlide(deck.meta.slides.length - 1)
        }
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [
    auth.canRecord,
    deck.meta.slides.length,
    goRelative,
    goToSlide,
    isStudio,
    isStudioRoute,
    showStudioHook,
    showStudioSignoff
  ])

  const currentSlide = deck.meta.slides[notesIndex]
  const currentStudioTitle = studioCue === 'hook'
    ? '開始前 · 冒頭フック'
    : studioCue === 'signoff'
      ? '終了 · 決め台詞'
      : currentSlide.title
  const currentStudioNotes = studioCue === 'hook'
    ? '最初の一言で「なぜ続きを見るべきか」を伝える。右矢印で本編1枚目へ。'
    : studioCue === 'signoff'
      ? 'stay hungry, stay foolish. で締める。左矢印で本編最終スライドへ戻る。'
      : currentSlide.notes ?? 'このスライドにはまだノートがありません。'
  const currentStudioCount = studioCue === 'hook'
    ? `開始前 / ${deck.meta.slides.length}`
    : studioCue === 'signoff'
      ? `終了 / ${deck.meta.slides.length}`
      : `${notesIndex + 1} / ${deck.meta.slides.length}`
  const studioDisplayFrame = studioCue === 'hook'
    ? 0
    : studioCue === 'signoff'
      ? studioIntroFrames + totalFrames + Math.min(30, studioOutroFrames - 1)
      : getSlideSettledFrame(slideIndex) + studioIntroFrames

  const openRecordingFullscreen = useCallback(async () => {
    await recordingSurfaceRef.current?.requestFullscreen()
  }, [])

  // Project the slide itself. Unlike the recording surface this has no reserved
  // side panel, so an event projector shows the slide and nothing else.
  const toggleSlideFullscreen = useCallback(async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen()
      return
    }
    await slideFrameRef.current?.requestFullscreen()
  }, [])

  const playTimeline = useCallback(() => {
    if (isStudioRoute && auth.canRecord && isStudio) {
      targetIndexRef.current = 0
      setNotesIndex(0)
      if (slideIndex !== 0) {
        studioPlaybackResetRef.current = true
        setSlideIndex(0)
      }
      window.history.replaceState(null, '', '#1')
      setStudioCue('hook')
      setIsTimelinePlaying(true)
      playerRef.current?.seekTo(0)
      recordingPlayerRef.current?.seekTo(0)
      playerRef.current?.play()
      recordingPlayerRef.current?.play()
      return
    }

    playerRef.current?.play()
  }, [auth.canRecord, isStudio, isStudioRoute, slideIndex])

  const enterAudienceMode = useCallback(() => {
    setIsTimelinePlaying(false)
    pausePlayers()
    setStudioCue(null)
    setMode('audience')
  }, [pausePlayers])

  const enterStudioMode = useCallback(() => {
    setMode('studio')
    showStudioHook()
  }, [showStudioHook])

  useEffect(() => {
    const player = recordingPlayerRef.current
    if (!player || !isStudioRoute || !auth.canRecord) {
      return
    }

    const handleEnded = () => showStudioSignoff()
    player.addEventListener('ended', handleEnded)
    return () => player.removeEventListener('ended', handleEnded)
  }, [auth.canRecord, isStudioRoute, showStudioSignoff])

  const previewSignoff = useCallback(() => {
    if (!isStudioRoute || !auth.canRecord || !isStudio) {
      return
    }

    showStudioSignoff()
  }, [auth.canRecord, isStudio, isStudioRoute, showStudioSignoff])

  useEffect(() => {
    if (!isStudioRoute || !auth.canRecord) {
      return
    }

    const handler = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'r' || isEditableTarget(event.target)) {
        return
      }

      event.preventDefault()
      if (event.shiftKey) {
        previewSignoff()
        return
      }

      playTimeline()
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [auth.canRecord, isStudioRoute, playTimeline, previewSignoff])

  return (
    <main className="viewer-page">
      <header className="viewer-header">
        <button className="ghost-button" type="button" onClick={onBack}>
          <ArrowLeft size={18} aria-hidden />
          一覧
        </button>
        <div>
          <p>{deck.meta.status}</p>
          <h1>{deck.meta.title}</h1>
        </div>
        <div className="viewer-actions">
          <button type="button" onClick={toggleSlideFullscreen} title="スライドだけを全画面表示">
            <Fullscreen size={18} aria-hidden />
            全画面
          </button>
          {auth.canRecord ? (
            <button
              type="button"
              onClick={openPresenterNotes}
              title="別ウィンドウでノートを開く（この窓の送りに追従する）"
            >
              <StickyNote size={18} aria-hidden />
              ノート別窓
            </button>
          ) : null}
          {isStudioRoute && auth.canRecord ? (
            <>
              <button
                type="button"
                className={mode === 'audience' ? 'active' : ''}
                onClick={enterAudienceMode}
                title="閲覧表示"
              >
                <Fullscreen size={18} aria-hidden />
                閲覧
              </button>
              <button
                type="button"
                className={mode === 'studio' ? 'active' : ''}
                onClick={enterStudioMode}
                title="撮影用レイアウト"
              >
                <Film size={18} aria-hidden />
                撮影
              </button>
              <button
                type="button"
                onClick={openRecordingFullscreen}
                title="撮影サーフェスだけを全画面表示"
              >
                <MonitorCheck size={18} aria-hidden />
                全画面撮影
              </button>
            </>
          ) : null}
          {!isStudioRoute && auth.canRecord ? (
            <button type="button" onClick={onOpenStudio} title="撮影用レイアウト">
              <Film size={18} aria-hidden />
              撮影
            </button>
          ) : null}
          {deck.meta.youtube?.url ? (
            <a href={deck.meta.youtube.url} target="_blank" rel="noreferrer">
              <ExternalLink size={18} aria-hidden />
              動画
            </a>
          ) : null}
        </div>
      </header>

      {isStudioRoute && !auth.canRecord ? (
        <StudioLoginGate auth={auth} onAuthChange={onAuthChange} />
      ) : null}

      {isStudioRoute && !auth.canRecord ? null : (
        <section
          className={isStudio ? 'stage-shell studio' : 'stage-shell audience'}
          style={
            {
              '--slide-canvas-width': canvas.width,
              '--slide-canvas-height': canvas.height
            } as CSSProperties
          }
        >
          <div className="slide-frame" ref={slideFrameRef}>
            <Player
              ref={playerRef}
              component={isStudio ? StudioDeckTimeline : DeckTimeline}
              durationInFrames={Math.max(isStudio ? studioTotalFrames : totalFrames, 1)}
              compositionWidth={canvas.width}
              compositionHeight={canvas.height}
              fps={fps}
              initialFrame={isStudio ? studioDisplayFrame : getSlideSettledFrame(slideIndex)}
              controls={false}
              inputProps={{ deck }}
              style={{ width: '100%', height: '100%' }}
            />
            {isStudio && studioCue && !isTimelinePlaying ? (
              <div className="studio-cue-overlay" aria-hidden="true">
                <StudioBookend kind={studioCue} deckTitle={deck.meta.title} overlay />
              </div>
            ) : null}
            <button
              type="button"
              className="nav-zone nav-zone-left"
              onClick={() => goRelative(-1)}
              aria-label="前のスライド"
            >
              <ChevronLeft size={28} aria-hidden />
            </button>
            <button
              type="button"
              className="nav-zone nav-zone-right"
              onClick={() => goRelative(1)}
              aria-label="次のスライド"
            >
              <ChevronRight size={28} aria-hidden />
            </button>
          </div>

        {isStudio ? (
          <aside className="studio-panel" aria-label="Studio panel">
            <div className="studio-panel-section">
              <PanelRight size={22} aria-hidden />
              <h2>Studio panel</h2>
              <p>
                ここは将来、文字起こし、話者向けアドバイス、台本、撮影キューを表示するために予約しています。
              </p>
            </div>
            <div className="studio-panel-section">
              <StickyNote size={22} aria-hidden />
              <h2>{currentStudioTitle}</h2>
              <p>{currentStudioNotes}</p>
            </div>
            {deck.meta.youtube?.id ? (
              <div className="video-embed">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${deck.meta.youtube.id}`}
                  title={deck.meta.youtube.title ?? deck.meta.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            ) : null}
          </aside>
        ) : null}
        </section>
      )}

      {isStudioRoute && auth.canRecord ? (
        <section className="recording-inspector" aria-label="Recording output">
          <div className="recording-inspector-header">
            <div>
              <p>Recording output</p>
              <h2>1920 x 1080 capture surface</h2>
            </div>
            <span>
              slide 1280 x 1080 / reserved 640 x 1080 · R: 撮影再生 / Shift+R: 終了確認
            </span>
          </div>
          <div
            ref={recordingSurfaceRef}
            className="recording-surface"
            aria-label="Fullscreen recording surface"
          >
            <div className="recording-slide-frame">
              <Player
                ref={recordingPlayerRef}
                component={StudioDeckTimeline}
                durationInFrames={Math.max(studioTotalFrames, 1)}
                compositionWidth={canvas.width}
                compositionHeight={canvas.height}
                fps={fps}
                initialFrame={studioDisplayFrame}
                controls={false}
                inputProps={{ deck }}
                style={{ width: '100%', height: '100%' }}
              />
              {studioCue && !isTimelinePlaying ? (
                <div className="studio-cue-overlay" aria-hidden="true">
                  <StudioBookend kind={studioCue} deckTitle={deck.meta.title} overlay />
                </div>
              ) : null}
              <button
                type="button"
                className="recording-nav-zone recording-nav-zone-left"
                onClick={() => goRelative(-1)}
                aria-label="前のスライド"
              />
              <button
                type="button"
                className="recording-nav-zone recording-nav-zone-right"
                onClick={() => goRelative(1)}
                aria-label="次のスライド"
              />
            </div>
            <aside className="recording-reserved-area">
              <div className="recording-notes">
                <span className="recording-notes-count">
                  {currentStudioCount}
                </span>
                <h2 className="recording-notes-title">{currentStudioTitle}</h2>
                <p className="recording-notes-body">
                  {currentStudioNotes}
                </p>
                {studioCue === 'hook' ? (
                  <span className="recording-notes-next">
                    次 → {deck.meta.slides[0]?.title}
                  </span>
                ) : studioCue === null && deck.meta.slides[notesIndex + 1] ? (
                  <span className="recording-notes-next">
                    次 → {deck.meta.slides[notesIndex + 1].title}
                  </span>
                ) : studioCue === null ? (
                  <span className="recording-notes-next">
                    次 → 終了 · 決め台詞
                  </span>
                ) : null}
              </div>
            </aside>
          </div>
        </section>
      ) : null}

      {isStudioRoute ? null : <DeckOutro meta={deck.meta} />}

      {isStudioRoute && !auth.canRecord ? null : (
        <footer className="viewer-footer">
          <button type="button" onClick={() => goRelative(-1)}>
            <ChevronLeft size={18} aria-hidden />
            前へ
          </button>
          <span>
            {isStudio ? currentStudioCount : `${notesIndex + 1} / ${deck.meta.slides.length}`}
          </span>
          <button type="button" onClick={() => goRelative(1)}>
            次へ
            <ChevronRight size={18} aria-hidden />
          </button>
          <button type="button" onClick={playTimeline}>
            <Play size={18} aria-hidden />
            {isStudioRoute && auth.canRecord && isStudio ? '撮影再生' : '再生'}
          </button>
        </footer>
      )}
    </main>
  )
}

function StudioLoginGate({
  auth,
  onAuthChange
}: {
  auth: AuthState
  onAuthChange: (auth: AuthState) => void
}) {
  return (
    <section className="studio-login-gate">
      <Film size={42} aria-hidden />
      <h2>{auth.loading ? 'ログイン状態を確認中です' : '撮影モードはログインが必要です'}</h2>
      <p>
        このモードは資料作成者の撮影用です。ログインしていない人は通常の資料閲覧ページを利用できます。
      </p>
      <AuthControls auth={auth} onAuthChange={onAuthChange} />
    </section>
  )
}

function getInitialSlideIndex(deck: DeckBundle) {
  const hash = Number.parseInt(window.location.hash.replace('#', ''), 10)

  if (Number.isFinite(hash)) {
    return Math.max(0, Math.min(deck.meta.slides.length - 1, hash - 1))
  }

  return 0
}

function isEditableTarget(target: EventTarget | null) {
  return target instanceof HTMLElement && (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  )
}

function getSlideStarts(deck: DeckBundle) {
  let cursor = 0

  return deck.meta.slides.map((slide) => {
    const start = cursor
    cursor += slide.durationInFrames ?? 120
    return start
  })
}
